/* eslint-disable max-len */
import { Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import { isEmpty, isEqual, size, xorWith } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { EStavLeiPlatby, ILeiPrevod, ILeiRejstrikResp } from 'types/leiTypes';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import leiDetailApi from 'services/lei/leiDetail';
import leiRejstrkApi from 'services/lei/leiRejstrik';
import { leiPrevodSend } from 'services/lei/leiTransfer';

import { EPeriodType, ETypMatky, ETypZadostiLei } from 'enums/lei/leiDataEnums';
import { EEventType } from 'enums/lei/leiDetailDataEnums';
import { ELeiStavZadosti } from 'enums/lei/leiStavZadosti';
import { EMaterskaSpol } from 'enums/lei/materskaSpol';
import { ETypAdresy } from 'enums/lei/typAdresy';
import { ENotificationType } from 'enums/notificationType';
import { emailRegExp, phoneNumberRegExp } from 'enums/regExp';
import routePathEnum from 'enums/routePathEnum';

import DetailInfoPrehled from '../formularPages/PrehledPages/DetailInfoPrehled';
import FinalniMatkaPrehled from '../formularPages/PrehledPages/FinalniMatkaPrehled';
import PrimaMatkaPrehled from '../formularPages/PrehledPages/PrimaMatkaPrehled';
import PrimaMatka from '../formularPages/PrimaMatka';
import Profondy from '../formularPages/Profondy';
import UpravaLeiDetail from '../formularPages/upravaLeiDetail';
import Osoba from './Osoba';
import Platba from './Platba';
import Podpis from './Podpis';
import Prehled from './Prehled';
import ZadaniLei from './ZadaniLei';

interface IPrevodLei {}
export interface IRejstrikErrs {
    legalname: boolean;
    entitylegalformcode: boolean;
    legaladdress: boolean;
}

const PrevodLei: React.FC<IPrevodLei> = () => {
    const [form] = Form.useForm();
    const { setLevel } = useProfily();
    const { language, lei, cisStaty, platbaLei, NotificationComponent, aktivniFirma } = useContext(Context);
    const { navigateWorkaround } = useNavigateWorkaround();
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [goToNextStep, setGoToNextStep] = useState<boolean>(false);
    const [nextStepTrigger, setNextStepTrigger] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [matkyErr, setMatkyErr] = useState<boolean>(false);
    const [matkyFileListError, setMatkyFileListError] = useState<boolean>(false);
    const [rejstrikVals, setRejstrikVals] = useState<ILeiRejstrikResp>();
    const [rejstrikErrs, setRejstrikErrs] = useState<IRejstrikErrs>({
        legalname: false,
        entitylegalformcode: false,
        legaladdress: false
    });
    const [uploading, setUploading] = useState<boolean>(false);
    const formdata = lei?.prevodData;
    const [formData, setFormData] = useState<ILeiPrevod | undefined>({
        ...formdata,
        // typzadosti: ETypZadostiLei.RESIDENT_GOVERNMENT_ENTITY,
        entitycheck: formdata?.entitycheck || 'N/A'
    });
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [PrehledOnly] = useState<boolean>(
        lei?.stavZadosti === ELeiStavZadosti.HOTOVO ||
            lei?.stavZadosti === ELeiStavZadosti.KONTROLA ||
            lei?.stavZadosti === ELeiStavZadosti.PODPIS
    );
    const showPayment =
        lei?.prevodData?.spolecnost?.registrationstatus === 'LAPSED' ||
        dayjs(lei?.prevodData?.spolecnost?.nextrenewaldate).diff(dayjs(), 'week', true) < 6;

    const finalniMatkaPrehledData = formData?.matky?.filter(
        e => e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
    );
    const { FIRMA_PLAIN, LEI_PLATBA, LEI_DETAIL } = routePathEnum;
    const baseURL = `${FIRMA_PLAIN}${aktivniFirma?.iducet}`;

    const nextStepQueue = () => {
        setGoToNextStep(true);
        setNextStepTrigger(pv => !pv);
    };

    const next = () => {
        form.resetFields();
        setCurrentStep(currentStep + 1);
        setIsEdit(false);
        window.scrollTo(0, 0);
    };

    const prev = () => {
        form.resetFields();
        setCurrentStep(currentStep - 1);
        window.scrollTo(0, 0);
    };

    const backToPrehled = () => {
        form.resetFields();
        setIsEdit(false);
    };

    useEffect(() => {
        setLoading(false);
    }, []);

    useEffect(() => {
        if (!rejstrikVals && formData?.spolecnost && formData?.spolecnost?.registrationauthorityentityid) {
            setLoading(true);
            leiRejstrkApi({
                ic: formData?.spolecnost?.registrationauthorityentityid || '',
                stat: formData?.spolecnost.jurisdikce || '',
                lang: language,
                regauthor: formData.spolecnost.registrationauthorityid || ''
            })
                .then(resp => setRejstrikVals(resp))
                .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]);

    useEffect(() => {
        const DataAddress = formData?.spolecnost?.adresy
            ? formData?.spolecnost?.adresy.filter(e => e.typadresy === ETypAdresy.LEGALADDRESS)[0]
            : {
                  firstaddressline: undefined,
                  city: undefined,
                  postalcode: undefined,
                  country: undefined
              };
        const RejstrikAddress = rejstrikVals?.adresy
            ? rejstrikVals.adresy.filter(e => e.typadresy === ETypAdresy.LEGALADDRESS)[0]
            : {
                  firstaddressline: undefined,
                  city: undefined,
                  postalcode: undefined,
                  country: undefined
              };
        const legalNameEvent =
            formData?.events && formData.events.length > 0
                ? formData.events.filter(e => e.eventtype === EEventType.CHANGE_LEGAL_NAME).length > 0
                : false;
        const legalFormEvent =
            formData?.events && formData.events.length > 0
                ? formData.events.filter(e => e.eventtype === EEventType.CHANGE_LEGAL_FORM).length > 0
                : false;
        const legalAddressEvent =
            formData?.events && formData.events.length > 0
                ? formData.events.filter(e => e.eventtype === EEventType.CHANGE_LEGAL_ADDRESS).length > 0
                : false;

        setRejstrikErrs({
            legalname:
                (formData?.spolecnost?.legalname || '').split(' ').join('') !==
                    (rejstrikVals?.legalname || '').split(' ').join('') &&
                !!rejstrikVals?.legalname &&
                !legalNameEvent,
            entitylegalformcode:
                (formData?.spolecnost?.entitylegalformcode || '').split(' ').join('') !==
                    (rejstrikVals?.entitylegalformcode || '').split(' ').join('') &&
                !!rejstrikVals?.entitylegalformcode &&
                !legalFormEvent,
            legaladdress:
                ((DataAddress.firstaddressline || '').split(' ').join('') !==
                    (RejstrikAddress.firstaddressline || '').split(' ').join('') &&
                    !!RejstrikAddress.firstaddressline) ||
                ((DataAddress.city || '').split(' ').join('') !== (RejstrikAddress?.city || '').split(' ').join('') &&
                    !!RejstrikAddress.city) ||
                ((DataAddress.postalcode || '').split(' ').join('') !==
                    (RejstrikAddress?.postalcode || '').split(' ').join('') &&
                    !!RejstrikAddress.postalcode) ||
                ((DataAddress.country || '').split(' ').join('') !==
                    (RejstrikAddress?.country || '').split(' ').join('') &&
                    !!RejstrikAddress.country &&
                    !legalAddressEvent)
        });
    }, [rejstrikVals, formData]);

    const primaMatkaData = formData?.matky?.filter(
        e => e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY || e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF
    );
    const internationalMatkaData = formData?.matky?.filter(e => e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF);

    const isPrimaFinalniMatkaFileArraySame = function (x: string[], y: string[]) {
        const isSameSize = size(x) === size(y);
        return isSameSize && isEmpty(xorWith(x, y, isEqual));
    };
    const [areFilesEqual, setAreFilesEqual] = useState<boolean>(
        primaMatkaData?.length &&
            primaMatkaData[0].informacekmatce?.soubory &&
            finalniMatkaPrehledData?.length &&
            finalniMatkaPrehledData[0].informacekmatce?.soubory
            ? isPrimaFinalniMatkaFileArraySame(
                  primaMatkaData[0].informacekmatce?.soubory.map(e => e.orignazev),
                  finalniMatkaPrehledData[0].informacekmatce?.soubory.map(e => e.orignazev)
              )
            : false
    );

    const matkySubmit = (goNext?: boolean) => {
        const submit = () => {
            setMatkyErr(false);
            const fieldsValue = form.getFieldsValue();
            const primaMatkaInformace = formData?.matky
                ?.filter(
                    e =>
                        e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY ||
                        e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF
                )
                .map(e => {
                    return {
                        informacekmatce: {
                            ...e.informacekmatce,
                            validationdocuments: fieldsValue.pvalidationdocuments,
                            ...(fieldsValue.pvalidationreference && {
                                validationreference: fieldsValue.pvalidationreference
                            }),
                            relationshipperiod: [
                                ...(fieldsValue.paccountingstart
                                    ? [
                                          {
                                              periodtype: EPeriodType.ACCOUNTING_PERIOD,
                                              ...(fieldsValue.paccountingstart && {
                                                  startdate: fieldsValue.paccountingstart.format('YYYY-MM-DD')
                                              }),
                                              ...(fieldsValue.paccountingend && {
                                                  enddate: fieldsValue.paccountingend.format('YYYY-MM-DD')
                                              })
                                          }
                                      ]
                                    : []),
                                ...(fieldsValue.pdocumentstart
                                    ? [
                                          {
                                              periodtype: EPeriodType.DOCUMENT_FILING_PERIOD,
                                              ...(fieldsValue.pdocumentstart && {
                                                  startdate: fieldsValue.pdocumentstart.format('YYYY-MM-DD')
                                              }),
                                              ...(fieldsValue.pdocumentend && {
                                                  enddate: fieldsValue.pdocumentend.format('YYYY-MM-DD')
                                              })
                                          }
                                      ]
                                    : []),
                                ...(fieldsValue.prelationship
                                    ? [
                                          {
                                              periodtype: EPeriodType.RELATIONSHIP_PERIOD,
                                              startdate: dayjs(fieldsValue?.prelationship).format('YYYY-MM-DD')
                                          }
                                      ]
                                    : [])
                            ]
                        }
                    };
                });
            const finalniMatkaInformace = formData?.matky
                ?.filter(e => e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY)
                .map(e => {
                    return {
                        informacekmatce: {
                            ...e.informacekmatce,
                            ...(((fieldsValue.fvalidationreference && !areFilesEqual) ||
                                (areFilesEqual && fieldsValue.pvalidationreference)) && {
                                validationdocuments: areFilesEqual
                                    ? fieldsValue.pvalidationreference
                                    : fieldsValue.fvalidationreference
                            }),
                            ...(((fieldsValue.fvalidationdocuments && !areFilesEqual) ||
                                (areFilesEqual && fieldsValue.pvalidationdocuments)) && {
                                validationdocuments: areFilesEqual
                                    ? fieldsValue.pvalidationdocuments
                                    : fieldsValue.fvalidationdocuments
                            }),
                            relationshipperiod: [
                                ...(fieldsValue.faccountingstart && !areFilesEqual
                                    ? [
                                          {
                                              periodtype: EPeriodType.ACCOUNTING_PERIOD,
                                              ...(fieldsValue.faccountingstart && {
                                                  startdate: fieldsValue.faccountingstart.format('YYYY-MM-DD')
                                              }),
                                              ...(fieldsValue.faccountingend && {
                                                  enddate: fieldsValue.faccountingend.format('YYYY-MM-DD')
                                              })
                                          }
                                      ]
                                    : areFilesEqual && fieldsValue.paccountingstart
                                      ? [
                                            {
                                                periodtype: EPeriodType.ACCOUNTING_PERIOD,
                                                ...(fieldsValue.paccountingstart && {
                                                    startdate: fieldsValue.paccountingstart.format('YYYY-MM-DD')
                                                }),
                                                ...(fieldsValue.paccountingend && {
                                                    enddate: fieldsValue.paccountingend.format('YYYY-MM-DD')
                                                })
                                            }
                                        ]
                                      : []),
                                ...(fieldsValue.fdocumentstart && !areFilesEqual
                                    ? [
                                          {
                                              periodtype: EPeriodType.DOCUMENT_FILING_PERIOD,
                                              ...(fieldsValue.fdocumentstart && {
                                                  startdate: fieldsValue.fdocumentstart.format('YYYY-MM-DD')
                                              }),
                                              ...(fieldsValue.fdocumentend && {
                                                  enddate: fieldsValue.fdocumentend.format('YYYY-MM-DD')
                                              })
                                          }
                                      ]
                                    : areFilesEqual && fieldsValue.pdocumentstart
                                      ? [
                                            {
                                                periodtype: EPeriodType.DOCUMENT_FILING_PERIOD,
                                                ...(fieldsValue.pdocumentstart && {
                                                    startdate: fieldsValue.pdocumentstart.format('YYYY-MM-DD')
                                                }),
                                                ...(fieldsValue.pdocumentend && {
                                                    enddate: fieldsValue.pdocumentend.format('YYYY-MM-DD')
                                                })
                                            }
                                        ]
                                      : []),
                                ...(finalniMatkaPrehledData &&
                                finalniMatkaPrehledData.length &&
                                fieldsValue.prelationship
                                    ? [
                                          {
                                              periodtype: EPeriodType.RELATIONSHIP_PERIOD,
                                              startdate: fieldsValue.prelationship.format('YYYY-MM-DD')
                                          }
                                      ]
                                    : [])
                            ]
                        }
                    };
                });
            const updatedMatky = formData?.matky?.map(e =>
                e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY ||
                e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF
                    ? {
                          ...e,
                          ...(e.informacekmatce && primaMatkaInformace?.length && primaMatkaInformace[0])
                      }
                    : e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
                      ? e.stav === EMaterskaSpol.ANO_SAME
                          ? {
                                ...e,
                                ...(e.informacekmatce && primaMatkaInformace?.length && primaMatkaInformace[0])
                            }
                          : {
                                ...e,
                                ...(e.informacekmatce && finalniMatkaInformace?.length && finalniMatkaInformace[0])
                            }
                      : e
            );
            setFormData(pv => {
                return {
                    ...pv,
                    matky: updatedMatky
                };
            });
            goNext ? next() : setIsEdit(true);
        };

        if (
            formData?.matky?.filter(
                e =>
                    e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY ||
                    e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
            ).length !== 2 &&
            formData?.typzadosti !== ETypZadostiLei.BRANCH &&
            goNext
        ) {
            setMatkyErr(true);
        } else {
            goNext ? form.validateFields().then(submit) : submit();
        }
    };

    console.log(formData);

    const steps = [
        {
            title: '',
            content: <ZadaniLei nextStep={next} setFormData={setFormData} />
        },
        {
            title: '',
            content: (
                <Osoba
                    nextStep={nextStepQueue}
                    setFormData={setFormData}
                    formDataOsoba={formData?.osoba}
                    formDataSpolecnost={formData?.spolecnost}
                />
            )
        },
        {
            title: 'LEI_DETAIL_INFO_TITLE_TABLE',
            content: (
                <>
                    <DetailInfoPrehled
                        formDataSpolecnost={formData?.spolecnost}
                        typZadosti={formData?.typzadosti}
                        rejstrikErrs={rejstrikErrs}
                    />
                    <UpravaLeiDetail
                        nextStep={nextStepQueue}
                        prevStep={prev}
                        setCurrentStep={setCurrentStep}
                        formData={formData}
                        setFormData={setFormData}
                        setIsEdit={setIsEdit}
                        cisStaty={cisStaty}
                        rejstrikVals={rejstrikVals}
                        rejstrikErrs={rejstrikErrs}
                    />
                </>
            )
        },
        {
            title: 'LEI_DETAIL_MAT_TITLE',
            content:
                isEdit || !primaMatkaData?.length ? (
                    <PrimaMatka
                        nextStep={nextStepQueue}
                        prevStep={backToPrehled}
                        setFormData={setFormData}
                        isEdit={isEdit}
                        cisStaty={cisStaty}
                        primaMatkaData={primaMatkaData?.length ? primaMatkaData : undefined}
                        internationalMatka={internationalMatkaData?.length ? internationalMatkaData : undefined}
                        typZadosti={formData?.typzadosti}
                        finalniMatkaData={formData?.matky?.filter(
                            e => e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
                        )}
                    />
                ) : (
                    <>
                        <PrimaMatkaPrehled
                            cisStaty={cisStaty}
                            upravaForm={form}
                            isEdit={isEdit}
                            setFormData={setFormData}
                            internationalMatkaData={internationalMatkaData?.length ? internationalMatkaData : undefined}
                            //    getCountryText={getCountryText}
                            //  getRegionText={getRegionText}
                            primaMatkaData={primaMatkaData?.length ? primaMatkaData : undefined}
                            setUploading={setUploading}
                        />
                        {finalniMatkaPrehledData?.length ||
                        (primaMatkaData && primaMatkaData.length > 0 && primaMatkaData[0].stav === EMaterskaSpol.NE) ? (
                            <>
                                <Title
                                    level={4}
                                    text="LEI_FINAL_TITLE"
                                    style={{
                                        marginTop: 10
                                    }}
                                />
                                <FinalniMatkaPrehled
                                    cisStaty={cisStaty}
                                    disabled={primaMatkaData && primaMatkaData[0].stav === EMaterskaSpol.NE}
                                    upravaForm={form}
                                    isEdit={isEdit}
                                    primaMatkaStav={
                                        (primaMatkaData && primaMatkaData.length && primaMatkaData[0].stav) || undefined
                                    }
                                    primaMatkaData={primaMatkaData?.length ? primaMatkaData : undefined}
                                    setFormData={setFormData}
                                    //  getCountryText={getCountryText}
                                    //  getRegionText={getRegionText}
                                    finalniMatkaData={formData?.matky?.filter(
                                        e => e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
                                    )}
                                    areFilesEqual={areFilesEqual}
                                    setAreFilesEqual={setAreFilesEqual}
                                    setUploading={setUploading}
                                />
                            </>
                        ) : null}
                        {isEdit === false && (
                            <Row>
                                <ButtonComponent
                                    onClick={() => {
                                        prev();
                                        setMatkyFileListError(false);
                                        setMatkyErr(false);
                                    }}
                                    buttontext="ZPET"
                                    type="default"
                                    style={{
                                        marginRight: 8
                                    }}
                                    id="editZpetBtn"
                                />
                                <ButtonComponent
                                    onClick={() => matkySubmit()}
                                    buttontext="BUTTON_UPRAVIT"
                                    type="primary"
                                    id="matkySubmitBtn"
                                />
                                <ButtonComponent
                                    style={{
                                        margin: '0 8px'
                                    }}
                                    type="primary"
                                    htmlType="button"
                                    loading={uploading}
                                    buttontext={'POKRACOVAT'}
                                    extramessage={
                                        matkyFileListError
                                            ? 'SOUBOR_NOT_LOADED'
                                            : matkyErr
                                              ? 'LEI_AKTUALIZACE_MATKA_ERR'
                                              : ''
                                    }
                                    onClick={() => matkySubmit(true)}
                                    id="editPokracovatBtn"
                                />
                            </Row>
                        )}
                    </>
                )
        },
        ...(formData?.typzadosti === 'FUND' || lei?.prevodData?.typzadosti === 'FUND'
            ? [
                  {
                      title: 'LEI_FOND_TITLE',
                      content: (
                          <Profondy
                              nextStep={nextStepQueue}
                              prevStep={prev}
                              setFormData={setFormData}
                              vsechnyMatky={formData?.matky}
                          />
                      )
                  }
              ]
            : []),
        {
            title: 'PREHLED',
            content: <Prehled PrehledOnly={PrehledOnly} nextStep={nextStepQueue} prevStep={prev} formData={formData} />
        },
        {
            title: '',
            content: <Podpis nextStep={nextStepQueue} prevStep={prev} setFormData={setFormData} formData={formData} />
        },
        ...(showPayment
            ? [
                  {
                      title: '',
                      content: <Platba prevStep={prev} formData={formData} setFormData={setFormData} />
                  }
              ]
            : [])
    ];

    useEffect(() => {
        console.log(currentStep, steps, goToNextStep, lei);
        if (!lei?.idVarsymZadosti?.id) {
            setCurrentStep(0);
        }
        if (PrehledOnly) return;
        if (
            lei?.idVarsymZadosti?.id &&
            currentStep === 1 &&
            formData?.osoba?.typosoby &&
            (!emailRegExp.test(formData?.osoba?.contactemail || '') ||
                !emailRegExp.test(formData?.osoba?.fakturacniemail || '') ||
                !phoneNumberRegExp.test(formData?.osoba?.contactphone || ''))
        ) {
            setIsEdit(true);
        } else if (lei?.idVarsymZadosti?.id && currentStep !== steps.length - 1 && !formData?.platba?.stavplatby) {
            console.log(goToNextStep, 'huh');
            setLoading(true);
            leiPrevodSend(
                lei?.idVarsymZadosti?.varsym ? lei.idVarsymZadosti.varsym : '0',
                lei?.idVarsymZadosti?.id ? lei.idVarsymZadosti.id : 0,
                'put',
                {
                    ...formData,
                    language: language
                }
            )
                .then(() => {
                    if (goToNextStep) {
                        next();
                        setGoToNextStep(false);
                    }
                })
                .finally(() => setLoading(false));
        } else if (currentStep === steps.length - 1) {
            setLoading(true);
            if (lei?.prevodData?.platba?.idplatby && formData?.platba?.typplatby === 'KARTOU')
                window.location.replace(
                    `https://payments.comgate.cz/specify/card/${lei?.prevodData?.platba?.idplatby}`
                );
            else
                leiPrevodSend(
                    lei?.idVarsymZadosti?.varsym ? lei.idVarsymZadosti.varsym : '0',
                    lei?.idVarsymZadosti?.id ? lei.idVarsymZadosti.id : 0,
                    'post',
                    {
                        ...formData,
                        language: language
                    }
                )
                    .then(resp => {
                        formData?.platba?.typplatby === 'KARTOU'
                            ? resp.url && (window.location.replace(resp.url), setLoading(false))
                            : (() =>
                                  leiDetailApi(lei?.idVarsymZadosti?.varsym || '0')
                                      .then(responzo => {
                                          setLevel('lei', {
                                              ...lei,
                                              detailData: responzo,
                                              paymentDueDate: responzo.paymentduedate
                                          });
                                          NotificationComponent({
                                              title: 'ZADOST_O_LEI',
                                              description: 'ZADOST_ODESLANA_LEI',
                                              type: ENotificationType.SUCCESS
                                          });
                                          if (formData?.platba?.typplatby === 'PREVODEM') {
                                              navigateWorkaround(`${baseURL}${LEI_PLATBA}`);
                                          } else {
                                              navigateWorkaround(`${baseURL}${LEI_DETAIL}`);
                                          }
                                      })
                                      .catch(() => {
                                          navigateWorkaround(`${baseURL}${LEI_DETAIL}`);
                                      })
                                      .finally(() => {
                                          setLoading(false);
                                      }))();
                    })
                    .catch(() => {})
                    .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, lei?.prevodData, nextStepTrigger]);

    useEffect(() => {
        if (PrehledOnly) setCurrentStep(steps.length - 3);
        else if (platbaLei) setCurrentStep(steps.length - 1);
        else if (
            lei?.prevodData?.platba?.stavplatby &&
            (lei.prevodData.platba.stavplatby === EStavLeiPlatby.PLODLO ||
                lei.prevodData.platba.stavplatby === EStavLeiPlatby.CEKA ||
                lei.prevodData.platba.stavplatby === EStavLeiPlatby.VYPR)
        )
            setCurrentStep(steps.length - 1);
        else if (formData?.typzadosti) setCurrentStep(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => setLevel('platbaLei', null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LoadingWrapper loading={loading}>
            <Title level={3} text={PrehledOnly ? 'LEI_PREVOD_TITLE_HOTOVO' : 'LEI_PREVOD_TITLE'} />
            {currentStep > 1 && (
                <InputComponent
                    label="LEI_PREVOD_LABEL"
                    name="lei"
                    defaultValue={formData?.spolecnost?.legalentityidentifier}
                    disabled={true}
                    notRequired={true}
                    labelCol={{
                        span: 6
                    }}
                />
            )}
            {<Title level={4} text={steps[currentStep].title} />}
            <Row gutter={8}>
                <Col span={24}>{steps[currentStep].content}</Col>
            </Row>
        </LoadingWrapper>
    );
};

export default PrevodLei;
